import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PublicLayout } from "@/components/site/PublicLayout";
import { IssueCard } from "@/components/site/IssueCard";
import { HumanCountBanner } from "@/components/site/HumanCountBanner";
import { StoreButtons } from "@/components/site/StoreButtons";
import { SupportCohnect } from "@/components/site/SupportCohnect";
import { CohnectMark } from "@/components/site/CohnectMark";
import { Users, Link2, Lock, BookOpen } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
};
const clip = (t, n = 180) => (t && t.length > n ? `${t.slice(0, n).trimEnd()}…` : t);

export default function HomePage() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/issues?limit=4").then((r) => setIssues(r.data || [])).finally(() => setLoading(false));
  }, []);

  const featured = issues[0];
  const others = issues.slice(1, 4);

  const scrollToNewsletter = () => {
    const el = document.getElementById("newsletter");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const steps = [
    { n: "01", t: "Get a prompt", b: "Each week, one simple question brings someone to mind." },
    { n: "02", t: "Send a simple message", b: "Use a copy-and-paste script or write your own." },
    { n: "03", t: "See how others are cohnecting", b: "Join the Discord community and share the moment." },
  ];

  const appFeatures = [
    { icon: Users, t: "People, places & things", b: "Add the humans, spots, and objects that matter and see them all in one living map." },
    { icon: Link2, t: "Plain-language links", b: "Connect anything in your own words — “introduced me to,” “works at,” “lives in.”" },
    { icon: Lock, t: "Private to you", b: "No account, no ads, no feeds. Your map stays on your device and belongs to you." },
  ];

  return (
    <PublicLayout>
      {/* TOP — two balanced boxes (no big headline) */}
      <section className="relative overflow-hidden hero-wash border-b-2 border-[color:var(--coh-ink)]">
        <CohnectMark className="pointer-events-none absolute -right-24 -top-28 w-[520px] text-[color:var(--coh-ink)] opacity-[0.05]" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="grid gap-6 lg:gap-8 lg:grid-cols-2 items-stretch">
            {/* APP BOX (primary) */}
            <div data-testid="home-app-box" className="nb-box h-full flex flex-col p-5 sm:p-6">
              <div className="flex items-center justify-between gap-3">
                <div className="kicker">The Cohnect App</div>
                <span data-testid="app-coming-soon" className="inline-flex items-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-blue)] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--coh-on-accent)]">
                  Coming Soon
                </span>
              </div>
              <div className="mt-4 border-2 border-[color:var(--coh-ink)] overflow-hidden bg-[color:var(--coh-paper)]">
                <img src="/brand/feature-map.png" alt="A Cohnect map showing how people, places and events connect" className="block w-full h-[210px] sm:h-[240px] object-cover object-top" />
              </div>
              <h2 className="mt-5 font-display text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight text-[color:var(--coh-ink)]">
                Remember how your world connects.
              </h2>
              <p className="mt-3 text-sm sm:text-base text-[color:var(--coh-ink-muted)] leading-relaxed">
                A private, interactive map of the people, places, and things in your life — link them in
                plain words and watch your world grow.
              </p>
              <div className="mt-auto pt-6">
                <StoreButtons />
                <p className="mt-3 text-xs text-[color:var(--coh-ink-muted)] font-mono uppercase tracking-[0.14em]">Free · No account · Private to you</p>
              </div>
            </div>

            {/* NEWSLETTER BOX (supporting) */}
            <button
              type="button"
              onClick={scrollToNewsletter}
              data-testid="home-newsletter-box"
              aria-label="Read this week’s letter"
              className="nb-box nb-interactive h-full flex flex-col p-5 sm:p-6 text-left"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="kicker inline-flex items-center gap-2"><BookOpen className="h-3.5 w-3.5" /> The Weekly Letter</div>
                <span className="inline-flex items-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-paper-2)] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--coh-ink)]">
                  Free
                </span>
              </div>

              {loading ? (
                <div className="mt-4 flex-1">
                  <div className="h-4 w-32 bg-[color:var(--coh-paper)] border-2 border-[color:var(--coh-ink)] animate-pulse" />
                  <div className="mt-4 h-8 w-3/4 bg-[color:var(--coh-paper)] border-2 border-[color:var(--coh-ink)] animate-pulse" />
                  <div className="mt-4 h-20 w-full bg-[color:var(--coh-paper)] border-2 border-[color:var(--coh-ink)] animate-pulse" />
                </div>
              ) : featured ? (
                <div className="mt-4 flex-1">
                  <div className="flex items-center gap-2 text-xs text-[color:var(--coh-ink-muted)] font-mono">
                    <span className="text-[color:var(--coh-blue)]">#{String(featured.issue_number).padStart(3, "0")}</span>
                    <span>·</span>
                    <span>{formatDate(featured.published_at || featured.updated_at)}</span>
                    <span className="ml-1 inline-flex items-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-blue)] px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em] text-[color:var(--coh-on-accent)]">This week</span>
                  </div>
                  <h2 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight text-[color:var(--coh-ink)]">
                    {featured.title}
                  </h2>
                  {featured.excerpt && (
                    <p className="mt-3 text-sm sm:text-base text-[color:var(--coh-ink-muted)] leading-relaxed">{clip(featured.excerpt, 150)}</p>
                  )}
                  {featured.inspiring_quote?.text ? (
                    <figure className="pull-quote mt-5" data-testid="newsletter-pullquote">
                      <blockquote className="font-display text-lg sm:text-xl font-bold leading-snug text-[color:var(--coh-ink)]">
                        “{featured.inspiring_quote.text}”
                      </blockquote>
                      {featured.inspiring_quote.author && (
                        <figcaption className="mt-2 text-xs font-mono uppercase tracking-[0.14em] text-[color:var(--coh-ink-muted)]">— {featured.inspiring_quote.author}</figcaption>
                      )}
                    </figure>
                  ) : (
                    <div className="mt-5 border-l-4 border-[color:var(--coh-blue)] pl-4" data-testid="newsletter-highlight">
                      <p className="font-display text-base sm:text-lg font-bold leading-snug text-[color:var(--coh-ink)]">{featured.theme ? `This week: ${featured.theme}` : "One reconnection prompt and one small action, inside."}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-4 flex-1 flex items-center">
                  <p className="text-base text-[color:var(--coh-ink-muted)] leading-relaxed">
                    A new issue is on its way. Check back soon — or browse the archive below.
                  </p>
                </div>
              )}

              <div className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--coh-blue)]">
                Read this week’s letter <span aria-hidden="true" className="font-mono">↓</span>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* APP SECTION — real app, phone screenshot */}
      <section id="app" className="bg-[color:var(--coh-paper-2)] border-b-2 border-[color:var(--coh-ink)] scroll-mt-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid gap-10 lg:gap-14 lg:grid-cols-[1.05fr,0.95fr] items-center">
            <div>
              <div className="kicker inline-flex items-center gap-2">The App <span className="inline-flex items-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-blue)] px-2 py-0.5 text-[10px] text-[color:var(--coh-on-accent)]">Coming Soon</span></div>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-[-0.03em] leading-[1.05] text-[color:var(--coh-ink)]">
                A living map of your connections.
              </h2>
              <p className="mt-5 text-lg text-[color:var(--coh-ink-muted)] max-w-xl leading-relaxed">
                Cohnect connects people, places, and things together in a web, with helpful features
                to help you build stronger relationships. Link anything in plain words and watch your
                map grow — private to you, always.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                {appFeatures.map((f) => {
                  const Icon = f.icon;
                  return (
                    <div key={f.t} className="nb-box p-5">
                      <div className="inline-flex h-10 w-10 items-center justify-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-blue)] text-[color:var(--coh-on-accent)]"><Icon className="h-5 w-5" /></div>
                      <h3 className="mt-4 font-display text-base font-extrabold text-[color:var(--coh-ink)]">{f.t}</h3>
                      <p className="mt-2 text-sm text-[color:var(--coh-ink-muted)] leading-relaxed">{f.b}</p>
                    </div>
                  );
                })}
              </div>
              <div className="mt-8">
                <StoreButtons />
                <p className="mt-3 text-xs text-[color:var(--coh-ink-muted)] font-mono uppercase tracking-[0.14em]">Free · No account · No ads · Private to you</p>
              </div>
            </div>
            <div className="justify-self-center">
              <div className="inline-block border-2 border-[color:var(--coh-ink)] shadow-nb bg-[color:var(--coh-ink)] p-2">
                <img src="/brand/phone-focus.png" alt="The Cohnect app showing a personal connection map" className="block w-full max-w-[280px] h-auto" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER SECTION — complementary */}
      <section id="newsletter" className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 scroll-mt-20">
        <div className="max-w-3xl">
          <div className="kicker inline-flex items-center gap-2"><BookOpen className="h-3.5 w-3.5" /> The Weekly Letter · Free</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[color:var(--coh-ink)]">
            Understand how connection works.
          </h2>
          <p className="mt-5 text-lg text-[color:var(--coh-ink-muted)] leading-relaxed">
            A free weekly letter exploring human connection — relationships, psychology, technology,
            and the interesting ways people connect around the world. Each issue brings one
            reconnection prompt and one small action you can finish in minutes.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/issues" className="btn-nb btn-nb-primary inline-flex h-11 items-center px-5 font-semibold">Browse the archive <span aria-hidden="true" className="font-mono ml-1">↗</span></Link>
          </div>
        </div>
      </section>

      <HumanCountBanner />

      {/* Support Cohnect */}
      <SupportCohnect />

      {/* How the letter works */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-16 pb-4">
        <div className="kicker">How the letter works</div>
        <h3 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[color:var(--coh-ink)]">Three small ideas, once a week.</h3>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {steps.map((s) => (
            <div key={s.n} className="nb-box p-6" data-testid={`home-howitworks-step-${s.n}`}>
              <div className="inline-flex items-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-paper-2)] px-2 py-1 font-mono text-xs font-semibold tracking-[0.14em] text-[color:var(--coh-ink)]">{s.n}</div>
              <h4 className="mt-4 font-display text-xl font-extrabold text-[color:var(--coh-ink)]">{s.t}</h4>
              <p className="mt-2 text-sm text-[color:var(--coh-ink-muted)] leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Recent issues */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <div className="kicker">Latest</div>
            <h3 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[color:var(--coh-ink)]">Recent issues</h3>
          </div>
          <Link to="/issues" data-testid="home-view-all-issues" className="text-sm font-semibold text-[color:var(--coh-blue)] underline underline-offset-4 decoration-2 hover:decoration-[3px]">
            View all <span aria-hidden="true" className="font-mono">↗</span>
          </Link>
        </div>
        {loading ? (
          <div className="h-40 nb-box animate-pulse" />
        ) : issues.length === 0 ? (
          <div className="nb-box p-8 text-center text-[color:var(--coh-ink-muted)]">No published issues yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured && <IssueCard issue={featured} featured />}
            {others.map((i) => <IssueCard key={i.id} issue={i} />)}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
